import { UPDATE, CREATE, useNotify } from 'react-admin';
import { useRunRequest } from '@/hooks';
import { HandleResponse } from '@/providers/turn/types';
import { TurnTargets } from '@/types/turn-api';
import { TurnActionsEnum, TurnResourcesEnum, TurnTargetsEnum } from '@/types/turn-enums';
import { ON_ACTION_TARGETS } from './constants';
import { openDashboardForCandidate } from './dashboardNavigation';

const useController = (id: string) => {
  const notify = useNotify();
  const { runRequest } = useRunRequest();

  const disableConsentLink = () =>
    runRequest({
      fetchType: UPDATE,
      resource: TurnResourcesEnum.CandidateConsentEmail,
      params: {
        id,
        target: TurnTargetsEnum.DisableConsentLink,
      },
      useResponseDialog: true,
    });

  const moveWorkerForward = () =>
    runRequest({
      fetchType: UPDATE,
      resource: TurnResourcesEnum.FcraWorkers,
      params: {
        id,
        target: TurnTargetsEnum.MoveWorkerForward,
      },
      useResponseDialog: true,
    });

  const openDashboard = async (url: string | null) => openDashboardForCandidate(url, notify);

  const rerunAddresses = () =>
    runRequest({
      fetchType: CREATE,
      resource: TurnResourcesEnum.FcraWorkers,
      params: {
        id,
        target: TurnTargetsEnum.RerunAddresses,
      },
      useResponseDialog: true,
    });

  const resumeChecks = () =>
    runRequest({
      fetchType: UPDATE,
      resource: TurnResourcesEnum.FcraWorkers,
      params: {
        id,
        target: TurnTargetsEnum.ResumeChecks,
      },
      mode: 'dialog',
      dialog: { title: 'Success!', message: 'Checks were resumed.' },
    });

  const sendAdverseNotice = () =>
    runRequest({
      fetchType: UPDATE,
      resource: TurnResourcesEnum.FcraWorkersAdverse,
      params: {
        id,
        target: TurnTargetsEnum.SendAdverse,
      },
    });

  const sendPreadverseNotice = () =>
    runRequest({
      fetchType: UPDATE,
      resource: TurnResourcesEnum.FcraWorkersAdverse,
      params: {
        id,
        target: TurnTargetsEnum.SendPreadverse,
      },
    });

  const setRecordPending = () =>
    runRequest({
      fetchType: UPDATE,
      resource: TurnResourcesEnum.FcraWorkers,
      params: {
        id,
        target: TurnTargetsEnum.Actions,
        action: TurnActionsEnum.Reviewed,
      },
    });

  const TARGET_TO_ACTION: Partial<Record<TurnTargets, () => Promise<HandleResponse>>> = {
    disable_consent_link: disableConsentLink,
    move_worker_forward: moveWorkerForward,
    rerun_addresses: rerunAddresses,
    resume_checks: resumeChecks,
    send_adverse: sendAdverseNotice,
    send_preadverse: sendPreadverseNotice,
    set_pending: setRecordPending,
  };

  const doAction = async (target: TurnTargets, onAction?: () => void) => {
    if (ON_ACTION_TARGETS.includes(target)) return onAction?.();

    const action = TARGET_TO_ACTION[target];

    if (!action) return console.error(`Target not recognized ${target}`);

    await action();
  };

  return {
    doAction,
    openDashboard,
  };
};

export default useController;
